import React from 'react';

import LandingPageLink from 'components/landing/LandingPageLink';
import useMartyContext from 'hooks/useMartyContext';
import MelodyGrid from 'components/landing/MelodyGrid';
import MelodyCurated from 'components/landing/MelodyCurated';
import LandingProductCardWrapper from 'components/landing/LandingProductCardWrapper';
import ProductList from 'components/landing/ProductList';
import ProductGrid from 'components/common/ProductGrid';
import ProductCarousel from 'components/common/ProductCarouselOld';
import ProductResults from 'components/landing/ProductResults';
import ProductImageInline from 'components/landing/ProductImageInline';
import MarketRecos from 'components/search/MarketRecos';
import type { HeartProps } from 'types/hearts';

import css from 'styles/components/landing/productSearch.scss';

export interface Product {
  imageMap: { [key: string]: string };
  brandName: string;
  productId: string;
  originalPrice: string;
  colorId: string;
  msaImageUrl?: string;
  percentOff: string;
  productTypeFacet?: string;
  productUrlRelative?: string;
  productName: string;
  thumbnailImageUrl?: string;
  styleId: string;
  price: string;
  msaImageId?: string;
  productUrl: string;
  productRating: number;
  thumbnailImageUrlRelative?: string;
}

export interface SlotDetails {
  ctacopy: string;
  gae: string;
  title: string;
  ctalink: string;
  eventLabel: string;
  componentName: string;
  siteName: string;
  style: string;
  products: Product[];
}

export interface ProductSearchProps {
  slotIndex: number;
  slotName: string;
  slotDetails: SlotDetails;
  slotHeartsData: HeartProps;
  onComponentClick: (evt: React.MouseEvent<HTMLAnchorElement>, product: Product) => void;
  shouldLazyLoad: boolean;
  isFullWidth: boolean;
  isOnSearchPage?: boolean;
}

// Contrary to the name.. this is used on other landing pages as well outside of Search
export const ProductSearch = (props: ProductSearchProps) => {
  const {
    marketplace: {
      landing: { hasProductSearchCarousel }
    }
  } = useMartyContext();
  const { testId } = useMartyContext();
  const { slotName, slotDetails, onComponentClick, shouldLazyLoad, slotHeartsData, isFullWidth, isOnSearchPage } = props;

  const { style, eventLabel: eventLabelCustom, componentName, gae, ctacopy, ctalink } = slotDetails || {};
  const eventLabel = eventLabelCustom || style || componentName;
  const commonProps = { ...props, eventLabel, shouldLazyLoad };

  switch (style) {
    case 'melody-grid':
      return <MelodyGrid {...commonProps} />;
    case 'melody-grid-half':
      return <MelodyGrid {...commonProps} isHalf={true} />;
    case 'melody-curated':
      return <MelodyCurated {...commonProps} />;
    case 'image-inline':
      return <ProductImageInline {...commonProps} />;
    case 'product-list':
      return <ProductList {...commonProps} />;
    case 'product-grid':
      return <ProductGrid {...commonProps} />; // Does not support cross site label and modal
    case 'default-results':
      return <ProductResults {...commonProps} />;
    case 'marketplace-carousel':
      return <MarketRecos crossSiteRecos={slotDetails} />;
    case 'carousel':
      if (hasProductSearchCarousel) {
        return <ProductCarousel {...commonProps} />; // Does not support cross site label and modal
      }
    // eslint-disable-next-line no-fallthrough
    default: // style === 'melody' or no style property provided
      return (
        <div data-test-id={testId('default-carousel')}>
          <LandingProductCardWrapper
            slotDetails={slotDetails}
            slotHeartsData={slotHeartsData}
            shouldLazyLoad={shouldLazyLoad}
            isFullWidth={isFullWidth}
            isCarousel={true}
            slotName={slotName}
            isOnSearchPage={isOnSearchPage}
          />
          {ctacopy && ctalink && (
            <LandingPageLink
              data-eventlabel={eventLabel}
              data-eventvalue={gae || ctacopy}
              className={css.cta}
              onClick={onComponentClick}
              url={ctalink}
            >
              {ctacopy}
            </LandingPageLink>
          )}
        </div>
      );
  }
};

export default ProductSearch;
